/**
 * Hit Feel Configuration — Tier Table (Hit-Feel Phase B, Slice 4).
 *
 * Consolidates screen shake, freeze (hitstop), sprite flash, and visual nudge
 * across all hit weights (tap, light, medium, heavy, epic) into a single
 * authoritative data table.
 *
 * Follows the defaults-plus-dev-override pattern (e.g. combat-feedback.config.ts).
 */

import type { HitWeight } from '../combat/hitWeight';

export interface HitFeelShake {
  /** Camera shake amplitude/intensity */
  readonly intensity: number;
  /** Duration of the shake in milliseconds */
  readonly durationMs: number;
}

export interface HitFeelFlash {
  /** Color mode: 'school' resolves target/school tint; 'white' is heavy/epic blowout */
  readonly colorMode: 'school' | 'white';
  /** Duration of the sprite flash in milliseconds */
  readonly durationMs: number;
}

export interface HitFeelTier {
  /** Camera shake; null = none. */
  readonly shake: HitFeelShake | null;
  /** Per-sprite micro-freeze (hitstop) on target in milliseconds. 0 = none. */
  readonly freezeMs: number;
  /** Target sprite flash; null = none. */
  readonly flash: HitFeelFlash | null;
  /** Visual-only nudge amplitude (px) — render offset. 0 = none. */
  readonly nudgePx: number;
  /** 1-frame (~50ms) screen overlay. Photosensitivity-gated. */
  readonly impactFrame: boolean;
  /** Impact visual effect scale multiplier. */
  readonly effectScale: number;
}

export const HIT_FEEL_TIERS_DEFAULTS: Record<HitWeight, HitFeelTier> = {
  tap: {
    shake: null,
    freezeMs: 0,
    flash: null,
    nudgePx: 0,
    impactFrame: false,
    effectScale: 0.8,
  },
  light: {
    shake: null,
    freezeMs: 0,
    flash: { colorMode: 'school', durationMs: 100 },
    nudgePx: 3,
    impactFrame: false,
    effectScale: 0.8,
  },
  medium: {
    shake: { intensity: 4, durationMs: 120 },
    freezeMs: 20,
    flash: { colorMode: 'school', durationMs: 100 },
    nudgePx: 6,
    impactFrame: false,
    effectScale: 1.2,
  },
  heavy: {
    shake: { intensity: 10, durationMs: 200 },
    freezeMs: 50,
    flash: { colorMode: 'white', durationMs: 100 },
    nudgePx: 10,
    impactFrame: false,
    effectScale: 2.0,
  },
  epic: {
    shake: { intensity: 20, durationMs: 350 },
    freezeMs: 100,
    flash: { colorMode: 'white', durationMs: 100 },
    nudgePx: 15,
    impactFrame: true,
    effectScale: 3.5,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to HIT_FEEL_TIERS_DEFAULTS.
 */
export const HIT_FEEL_TIERS_DEV_OVERRIDE: Partial<Record<HitWeight, Partial<HitFeelTier>>> = {};

function resolveTier(weight: HitWeight): HitFeelTier {
  const def = HIT_FEEL_TIERS_DEFAULTS[weight];
  const override = HIT_FEEL_TIERS_DEV_OVERRIDE[weight];
  if (!override) return def;
  return {
    ...def,
    ...override,
    shake: override.shake !== undefined ? override.shake : def.shake,
    flash: override.flash !== undefined ? override.flash : def.flash,
  };
}

export const HIT_FEEL_TIERS: Record<HitWeight, HitFeelTier> = {
  tap: resolveTier('tap'),
  light: resolveTier('light'),
  medium: resolveTier('medium'),
  heavy: resolveTier('heavy'),
  epic: resolveTier('epic'),
};
